import React, { useEffect, useState } from 'react';
import { formatLockScreenDate, formatLockScreenTime, normalizeLanguage } from '../utils/i18n';

const DEFAULT_LOCKSCREEN_BACKGROUND = '/assets/grass.jpg';

const resolveLockScreenBackground = (landingPage) => {
  const background = landingPage?.backgroundImage;

  if (typeof background === 'string' && background.startsWith('/')) {
    return background;
  }

  return DEFAULT_LOCKSCREEN_BACKGROUND;
};

const resolveLockScreenProfileImage = (profile) => {
  const profileImage = profile?.image || '';

  if (typeof profileImage === 'string' && profileImage.startsWith('/')) {
    return profileImage;
  }

  return '';
};

const resolveLockScreenName = (profile) => {
  const profileName = typeof profile?.name === 'string' ? profile.name.trim() : '';

  return profileName || 'Just nobody';
};

const LockScreen = ({ profile, landingPage, language, onUnlock, onAdminPortalOpen }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [resolvedBackground, setResolvedBackground] = useState(() => resolveLockScreenBackground(landingPage));
  const [resolvedProfileImage, setResolvedProfileImage] = useState(() => resolveLockScreenProfileImage(profile));
  const activeLanguage = normalizeLanguage(language);
  const lockScreenLogo = '/icons/door_open_white.png';

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const lockScreenBackground = resolveLockScreenBackground(landingPage);
  const lockScreenProfileImage = resolveLockScreenProfileImage(profile);
  const lockScreenName = resolveLockScreenName(profile);

  useEffect(() => {
    const image = new Image();
    let isCancelled = false;

    image.onload = () => {
      if (!isCancelled) {
        setResolvedBackground(lockScreenBackground);
      }
    };

    image.onerror = () => {
      if (!isCancelled) {
        setResolvedBackground(DEFAULT_LOCKSCREEN_BACKGROUND);
      }
    };

    image.src = lockScreenBackground;

    return () => {
      isCancelled = true;
    };
  }, [lockScreenBackground]);

  useEffect(() => {
    if (!lockScreenProfileImage) {
      setResolvedProfileImage('');
      return undefined;
    }

    const image = new Image();
    let isCancelled = false;

    image.onload = () => {
      if (!isCancelled) {
        setResolvedProfileImage(lockScreenProfileImage);
      }
    };

    image.onerror = () => {
      if (!isCancelled) {
        setResolvedProfileImage('');
      }
    };

    image.src = lockScreenProfileImage;

    return () => {
      isCancelled = true;
    };
  }, [lockScreenProfileImage]);

  return (
    <div
      className="lock-screen"
      style={{
        backgroundImage: `url(${resolvedBackground})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center'
      }}
    >
      <div className="clock-container">
        <div className="time-text">{formatLockScreenTime(currentTime, activeLanguage)}</div>
        <div className="date-text">{formatLockScreenDate(currentTime, activeLanguage)}</div>
      </div>

      <div className="login-container">
        <div className="profile-circle">
          {resolvedProfileImage ? (
            <img src={resolvedProfileImage} alt="profile" />
          ) : (
            <span className="profile-circle-fallback" aria-hidden="true">👤</span>
          )}
        </div>

        <h2 className="user-name">{lockScreenName}</h2>

        <div className="unlock-btn" onClick={onUnlock}>
          <span>Click to unlock</span>
          <span className="arrow">{'>'}</span>
        </div>
      </div>

      <button
        type="button"
        className="lock-screen-corner-logo"
        onClick={onAdminPortalOpen}
        aria-label="Open admin terminal authentication"
      >
        <img src={lockScreenLogo} alt="" aria-hidden="true" />
      </button>
    </div>
  );
};

export default LockScreen;
